import { useEffect, useRef, type RefObject } from 'react'
import {
  captureProjectThumbnailNow,
  registerProjectThumbnailCaptureSource,
} from '@/services/thumbnail-service'

/** Let the render settle (images decoded, layout stable) before rasterising. */
const SETTLE_DELAY_MS = 2_500

/**
 * Make the surface in `elementRef` the thumbnail source for `projectId`, and
 * take one capture once it has settled.
 *
 * Auto-save already schedules a debounced refresh after edits, but a project
 * the user merely opens never saves — and that is the common case for the
 * dashboard, where the card is the only thing the user ever sees. One capture
 * per open closes that gap.
 *
 * Nothing is stored when the render is blank; see
 * `project-thumbnail-capture.ts`.
 */
export function useProjectThumbnailAutoCapture(
  elementRef: RefObject<HTMLElement | null>,
  projectId: string | null | undefined,
  ready: boolean,
): void {
  const capturedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!projectId) return
    return registerProjectThumbnailCaptureSource(projectId, () => elementRef.current)
  }, [elementRef, projectId])

  useEffect(() => {
    if (!projectId || !ready) return
    if (capturedForRef.current === projectId) return
    const timer = setTimeout(() => {
      capturedForRef.current = projectId
      void captureProjectThumbnailNow(projectId).catch(() => {
        // Best-effort — a failed capture just leaves the honest placeholder.
        capturedForRef.current = null
      })
    }, SETTLE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [projectId, ready])
}
